/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useContext, useRef, useState, useEffect} from "react";

import {useDispatch} from "react-redux";
import {useTypedSelector} from "@Services/Hooks/UseTypedSelector";
import CHANGE_API_REQUEST_STATUS_ACTION from "@Actions/Interface/ChangeApiRequestStatus";

import ApplicationApiContext from "@Contexts/Api";
import APPLICATION_ROUTER_INTERFACE from "@ROUTER/Interfaces/Router";
import ApplicationRouterContext from "@Contexts/Router";

import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";

import {HTTP_ERROR_RESPONSE_INTERFACE} from "@Services/Interfaces/Http/Response/Error";

import DEFAULT_REQUEST_HEADERS from "@Services/Interfaces/Http/Request/Headers";

import {Redirect} from "react-router-dom";


import ErrorMessageComponent from "@Components/UI/Communicates/Messages/Error";


import BlockContentHeaderComponent from "@Components/UI/Containers/Block/Header";
import BlockContentContainerComponent from "@Components/UI/Containers/Block";

import ContactPagePhoneComponent from "@UIPages/Authenticated/Settings/Information/Contact/Phone";
import {ContactPageMailComponent} from "@UIPages/Authenticated/Settings/Information/Contact/Mail";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";


interface CONTACT_REASONS_INTERFACE {
    Order: number;
    Text: string;
}


interface CONTACT_TYPE_INTERFACE {
    Type: "M" | "P",
    Address: string,
    Reasons?: CONTACT_REASONS_INTERFACE[]
}


interface CONTACT_PAGE_INTERFACE {
    Contacts: CONTACT_TYPE_INTERFACE[]
}


type ContactPageProps = {};


export const SettingsContactPageComponent: React.FC<ContactPageProps> = (): JSX.Element | null => {


    /** @const Global State Actions Dispatcher */
    const DISPATCHER = useDispatch();


    /** @const {React.Ref} My Favourite Terminals Page Container DOM Ref */
    const COMPONENT_REF: React.Ref<HTMLDivElement | null> = useRef(null);


    /** @const {UserInterface | null} Application Logged User (from Global Store Object) */
    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state): ReturnType<any> => state?.authentication
    )?.loggedUser || null;


    /** @const {string} Global Rest Api Root URL Address */
    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;

    /** @const {object} REST_API_DATA - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.api
    );

    /** @const {} REST_API_REQUEST_TOKEN - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;


    const ROUTER: APPLICATION_ROUTER_INTERFACE = useContext(ApplicationRouterContext);


    const [CONTACT_PAGE_DATA, SET_CONTACT_PAGE_DATA] = useState<CONTACT_PAGE_INTERFACE | null>(null);
    const [CONTACT_PAGE_ERROR, SET_CONTACT_PAGE_ERROR] = useState<any | null>(null);

    useEffect(() => {

        if (!REST_API_URL || !LOGGED_USER?.userID) return;

        const CONTACT_PAGE_DATA_API_URL: string = REST_API_URL + "/App/Contact/List";

        const CONTACT_PAGE_DATA_REQUEST_DATA = {
            UserId: LOGGED_USER?.userID, // Logged User ID
        };

        const GET_CONTACT_PAGE_DATA = async (): Promise<void | Function> => {

            DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(true)); // Set Status to true

            try {

                const CONTACT_PAGE_RESPONSE = await REST_API_REQUEST(
                    CONTACT_PAGE_DATA_API_URL,
                    CONTACT_PAGE_DATA_REQUEST_DATA,
                    REST_API_REQUEST_TOKEN
                );

                if (CONTACT_PAGE_RESPONSE.status === 200) {
                    const CONTENT: CONTACT_PAGE_INTERFACE = await CONTACT_PAGE_RESPONSE.json();
                    SET_CONTACT_PAGE_DATA(CONTENT);
                } else {
                    const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await CONTACT_PAGE_RESPONSE.json();
                    SET_CONTACT_PAGE_ERROR({UserMessage: CONTENT?.UserMessage});
                }

            } catch (e) {
                SET_CONTACT_PAGE_ERROR({UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
            }

            DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(false)); // Set Status to false

        }

        GET_CONTACT_PAGE_DATA();

    }, [REST_API_URL]);


    useEffect(() => {
        return () => {
            SET_CONTACT_PAGE_DATA(null);
        };
    }, []);

    if (!LOGGED_USER || !LOGGED_USER?.userID) return <Redirect to={ROUTER.NOT_AUTHENTICATED.LOGIN.URL}/>;


    if (!ROUTER) return <Redirect to={"/404"}/>;


    const {Contacts: contactsTypes} = CONTACT_PAGE_DATA || {};


    const CONTACT_PAGE_TYPES = contactsTypes ? Object.fromEntries(contactsTypes.map((contact: any) => [contact.Type, contact])) : null;

    const MAIL_CONTACT_TYPE = CONTACT_PAGE_TYPES?.M || null;
    const PHONE_CONTACT_TYPE = CONTACT_PAGE_TYPES?.P || null;


    return (
        <BlockContentContainerComponent forwardRef={COMPONENT_REF} cssClasses={"settings-page-information-data-component contact-page-component settings-page-view-component"}>

            <BlockContentHeaderComponent headerText={"Kontakt"} />

            {!!CONTACT_PAGE_ERROR && <ErrorMessageComponent messageText={CONTACT_PAGE_ERROR.UserMessage} />}

            <ContactPageMailComponent contactData={MAIL_CONTACT_TYPE} />

            <ContactPagePhoneComponent contactData={PHONE_CONTACT_TYPE} />

        </BlockContentContainerComponent>
    );

}


export default SettingsContactPageComponent;
